import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import type { z } from 'zod';
import type { apiSocketCallPayloadSchema } from '@/services/chats/types';

/** The re-emittable `api_socket_call` payload for a sent chat message. */
export type OutboxPayload = z.infer<typeof apiSocketCallPayloadSchema>;

/** A sent chat message still awaiting server confirmation. */
export interface OutboxEntry {
  /** Shared id between the optimistic bubble and the socket payload. */
  uniqueMessageId: string;
  /** The exact `api_socket_call` payload, ready to re-emit on reconnect. */
  payload: OutboxPayload;
  /** When the message was first enqueued (ms epoch). */
  createdAt: number;
  /** How many times a (re)send has been attempted. */
  attempts: number;
}

/** Key under which the outbox is persisted in IndexedDB. */
export const CHAT_OUTBOX_PERSIST_KEY = 'chat-outbox';

interface ChatOutboxState {
  /** Pending entries keyed by uniqueMessageId. */
  entries: Record<string, OutboxEntry>;
  /** Add a message to the outbox (no-op if already queued). */
  enqueue: (uniqueMessageId: string, payload: OutboxPayload) => void;
  /** Remove a confirmed (or abandoned) message from the outbox. */
  remove: (uniqueMessageId: string) => void;
  /** Record a (re)send attempt. */
  markAttempt: (uniqueMessageId: string) => void;
  /** All pending entries, oldest first. */
  list: () => OutboxEntry[];
  /** Drop everything (used on logout). */
  clear: () => void;
}

/** IndexedDB-backed storage adapter for Zustand's persist middleware. */
const idbStateStorage: StateStorage = {
  getItem: async (name) => (await idbGet<string>(name)) ?? null,
  setItem: async (name, value) => {
    await idbSet(name, value);
  },
  removeItem: async (name) => {
    await idbDel(name);
  },
};

/**
 * Persisted outbox of chat messages awaiting server confirmation.
 *
 * A message is enqueued when it is sent (`sendMessageSocket`) and removed
 * when the server echo with a real id arrives (`chat-store` `updateMsg`).
 * Because the outbox is persisted to IndexedDB it survives a reload, and
 * `useChatOutbox` re-sends every pending entry whenever the socket
 * (re)connects — the web equivalent of the native app's `ChatWorker`.
 */
export const useChatOutboxStore = create<ChatOutboxState>()(
  persist(
    (set, get) => ({
      entries: {},

      enqueue: (uniqueMessageId, payload) => {
        if (get().entries[uniqueMessageId]) return;
        set((s) => ({
          entries: {
            ...s.entries,
            [uniqueMessageId]: {
              uniqueMessageId,
              payload,
              createdAt: Date.now(),
              attempts: 0,
            },
          },
        }));
      },

      remove: (uniqueMessageId) => {
        if (!get().entries[uniqueMessageId]) return;
        set((s) => {
          const next = { ...s.entries };
          delete next[uniqueMessageId];
          return { entries: next };
        });
      },

      markAttempt: (uniqueMessageId) => {
        const entry = get().entries[uniqueMessageId];
        if (!entry) return;
        set((s) => ({
          entries: {
            ...s.entries,
            [uniqueMessageId]: { ...entry, attempts: entry.attempts + 1 },
          },
        }));
      },

      list: () =>
        Object.values(get().entries).sort((a, b) => a.createdAt - b.createdAt),

      clear: () => set({ entries: {} }),
    }),
    {
      name: CHAT_OUTBOX_PERSIST_KEY,
      version: 1,
      storage: createJSONStorage(() => idbStateStorage),
      partialize: (state) => ({ entries: state.entries }),
    },
  ),
);
